import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useMutation, useApolloClient } from "@apollo/client";
import { PencilIcon } from "@heroicons/react/24/outline";
import { UPDATE_LIST } from "../../../graphql/mutation/List";
import { FETCH_LISTS } from "../../../graphql/query/List";

const EditableListName = ({ id, label, to, typeId, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(label || "");
  const [originalName, setOriginalName] = useState(label || "");
  const inputRef = useRef(null);
  const client = useApolloClient();

  const [updateList, { loading }] = useMutation(UPDATE_LIST({}), {
    refetchQueries: [
      {
        query: FETCH_LISTS(),
        variables: { input: { typeId } },
        fetchPolicy: "network-only",
      },
    ],
    awaitRefetchQueries: true,
    onCompleted: async () => {
      await client.refetchQueries({
        include: ["FetchLists"],
      });
      onUpdate?.();
    },
  });

  useEffect(() => {
    setName(label || "");
    setOriginalName(label || "");
  }, [label]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = (e) => {
    e.preventDefault();
    setIsEditing(true);
  };

  const handleEditClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    
    if (!trimmedName) {
      setName(originalName);
      setIsEditing(false);
      return;
    }

    if (trimmedName === originalName) {
      setIsEditing(false);
      return;
    }

    const res = await updateList({
      variables: {
        input: {
          id,
          name: trimmedName,
        },
      },
    });

    if (res?.data?.updateList?.success) {
      setOriginalName(trimmedName);
      setIsEditing(false);
    } else if (res?.data?.updateList?.error) {
      console.error("Error updating list name:", res.data.updateList.error);
      setName(originalName);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setName(originalName);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={loading}
        className="w-full border border-blue-500 rounded px-2 py-1 text-base font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
        style={{ minWidth: "200px" }}
      />
    );
  }

  return (
    <div className="flex items-center gap-2 group">
      <Link
        className="underline hover:no-underline cursor-pointer"
        to={to}
        onDoubleClick={handleDoubleClick}
        title="Double-click to edit name"
      >
        {label || "Untitled List"}
      </Link>
      <button
        onClick={handleEditClick}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
        title="Click to edit name"
        type="button"
      >
        <PencilIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
      </button>
    </div>
  );
};

export default EditableListName;

